import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CalendarCheck, Loader2, PhoneCall } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { BUSINESS, APPLIANCES, TIME_SLOTS } from "@/lib/data";
import Reveal from "@/components/Reveal";

const EMPTY = {
  name: "",
  phone: "",
  email: "",
  appliance: "",
  brand: "",
  preferred_date: "",
  preferred_time: "",
  address: "",
  notes: "",
};

const inputCls =
  "w-full bg-[#0F1215]/80 border border-white/10 rounded-xl px-4 py-3.5 text-sm text-[#F4F2EE] placeholder:text-[#606D7A] outline-none transition-colors duration-300 focus:border-[#E06A26]/60";
const labelCls = "block font-mono text-[10px] uppercase tracking-[0.25em] text-[#94A0AC] mb-2";

export default function BookingForm() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/bookings", { ...form, email: form.email || null, brand: form.brand || null, address: form.address || null, notes: form.notes || null });
      toast.success("Request received. We'll call to confirm your window shortly.");
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="booking-section" className="relative py-24 sm:py-32 hero-grid overflow-hidden">
      <div className="absolute -bottom-40 -right-40 w-[520px] h-[520px] rounded-full bg-[#E06A26]/10 blur-[140px] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#E06A26] mb-5">Book a repair</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">
              Request your service window.
            </h2>
            <p className="text-[#94A0AC] text-base leading-relaxed mt-6 max-w-md">
              Tell us what's failing and when suits you. We confirm every request personally by phone within one
              business hour — usually much faster.
            </p>
            <div className="mt-10 flex items-center gap-4 rounded-2xl border border-white/10 bg-[#171B20]/90 p-6 max-w-md">
              <span className="w-12 h-12 rounded-full bg-[#E06A26] flex items-center justify-center shrink-0">
                <PhoneCall className="w-5 h-5 text-[#0F1215]" />
              </span>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#606D7A]">Prefer to talk?</p>
                <a href={BUSINESS.phoneHref} data-testid="booking-phone-link" className="font-display text-xl font-bold hover:text-[#E06A26] transition-colors duration-300">
                  {BUSINESS.phone}
                </a>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15} className="lg:col-span-7">
          <form
            data-testid="booking-form"
            onSubmit={submit}
            className="rounded-2xl border border-white/10 bg-[#1E242B]/90 backdrop-blur-sm p-8 sm:p-10"
          >
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className={labelCls} htmlFor="bf-name">Full name *</label>
                <input id="bf-name" data-testid="booking-form-name-input" required minLength={2} className={inputCls} placeholder="Jane Cooper" value={form.name} onChange={set("name")} />
              </div>
              <div>
                <label className={labelCls} htmlFor="bf-phone">Phone *</label>
                <input id="bf-phone" data-testid="booking-form-phone-input" required minLength={7} className={inputCls} placeholder="(512) 555-0100" value={form.phone} onChange={set("phone")} />
              </div>
              <div>
                <label className={labelCls} htmlFor="bf-email">Email</label>
                <input id="bf-email" data-testid="booking-form-email-input" type="email" className={inputCls} placeholder="jane@example.com" value={form.email} onChange={set("email")} />
              </div>
              <div>
                <label className={labelCls} htmlFor="bf-appliance">Appliance *</label>
                <select id="bf-appliance" data-testid="booking-form-appliance-select" required className={inputCls} value={form.appliance} onChange={set("appliance")}>
                  <option value="" disabled>Select appliance</option>
                  {APPLIANCES.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls} htmlFor="bf-brand">Brand</label>
                <select id="bf-brand" data-testid="booking-form-brand-select" className={inputCls} value={form.brand} onChange={set("brand")}>
                  <option value="">Select brand (optional)</option>
                  {BUSINESS.brands.map((b) => <option key={b} value={b}>{b}</option>)}
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className={labelCls} htmlFor="bf-date">Preferred date *</label>
                <input id="bf-date" data-testid="booking-form-date-input" type="date" required className={`${inputCls} [color-scheme:dark]`} value={form.preferred_date} onChange={set("preferred_date")} />
              </div>
              <div>
                <label className={labelCls} htmlFor="bf-time">Preferred window *</label>
                <select id="bf-time" data-testid="booking-form-time-select" required className={inputCls} value={form.preferred_time} onChange={set("preferred_time")}>
                  <option value="" disabled>Select time window</option>
                  {TIME_SLOTS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls} htmlFor="bf-address">Service address</label>
                <input id="bf-address" data-testid="booking-form-address-input" className={inputCls} placeholder="Street, city, ZIP" value={form.address} onChange={set("address")} />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls} htmlFor="bf-notes">What's happening?</label>
                <textarea id="bf-notes" data-testid="booking-form-notes-input" rows={3} className={`${inputCls} resize-none`} placeholder="e.g. Fridge warm, freezer fine, clicking sound from the back…" value={form.notes} onChange={set("notes")} />
              </div>
            </div>

            <motion.button
              data-testid="booking-form-submit-button"
              type="submit"
              disabled={sending}
              whileTap={{ scale: 0.98 }}
              className="mt-8 w-full bg-[#E06A26] hover:bg-[#F07A34] disabled:opacity-60 text-[#0F1215] font-semibold py-4 rounded-full flex items-center justify-center gap-3 transition-all duration-300 hover:shadow-[0_0_32px_rgba(224,106,38,0.45)]"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarCheck className="w-4 h-4" />}
              {sending ? "Sending request…" : "Request my service window"}
            </motion.button>
            <p className="text-center font-mono text-[10px] uppercase tracking-[0.25em] text-[#606D7A] mt-5">
              No payment due now — confirmation call within 1 business hour
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
